/**
 * React.lazy() wrapper that survives a stale-chunk failure after a deploy.
 *
 * Vite gives each lazy-loaded route chunk a content-hashed filename. After a
 * deploy, the old chunk files are deleted from the server, but a browser that
 * still holds the previous index.html / entry chunk keeps referencing the old
 * filename — so the dynamic `import()` rejects ("Failed to fetch dynamically
 * imported module") and the route crashes with React Router's
 * "Unexpected Application Error".
 *
 * On such a failure we trigger a single cache-busting page reload. The reload
 * fetches the fresh index.html (and therefore the new chunk hashes), after which
 * the route loads normally. A sessionStorage guard prevents a reload loop when
 * the chunk is genuinely broken.
 *
 * The guard is cleared as soon as any lazy import succeeds, which is what makes it
 * recover more than once. Left set, it says "this tab has already tried" for the
 * whole life of the tab: the reload works, the operator carries on, and the NEXT
 * deploy's stale chunk is thrown straight at the error boundary instead of
 * reloading. Clearing on success scopes the guard to one unresolved failure, which
 * is what it was for.
 *
 * Usage is identical to React.lazy — only the import source changes:
 *   import { lazy } from "@/lib/lazy";
 *
 * @see https://vite.dev/guide/build#load-error-handling (dynamic-import reload)
 */
import {
  lazy as reactLazy,
  type ComponentType,
  type LazyExoticComponent,
} from "react";

const RELOAD_FLAG_KEY = "wicloud:chunk-reloaded";

/**
 * sessionStorage is absent in a non-browser render and can throw in a locked-down
 * browser, so every access goes through these two rather than being guarded at each
 * call site.
 */
function readReloadFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

function writeReloadFlag(value: boolean): void {
  try {
    if (value) {
      sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
    } else {
      sessionStorage.removeItem(RELOAD_FLAG_KEY);
    }
  } catch {
    // Nothing to do: a tab that cannot remember simply loses the loop guard.
  }
}

function isChunkLoadFailure(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message ?? "";
  return (
    // Vite / Chromium
    /Failed to fetch dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message) ||
    // Generic W3C
    /error loading dynamically imported module/i.test(message) ||
    // Safari (no message) sometimes surfaces a bare "Load failed"
    /^Load failed$/i.test(message)
  );
}

export function lazy<T extends ComponentType<unknown>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return reactLazy(() =>
    load()
      .then((module) => {
        // A chunk loaded, so whatever the last reload was for is behind us. Releasing
        // the guard here is what lets a tab still open at the next deploy recover by
        // itself instead of failing once first.
        if (readReloadFlag()) {
          writeReloadFlag(false);
        }

        return module;
      })
      .catch((error: unknown) => {
        if (!isChunkLoadFailure(error)) {
          throw error;
        }

        // Reload once; if we already reloaded and still hit the failure, surface
        // the error so the ErrorBoundary can render instead of looping forever.
        if (readReloadFlag()) {
          writeReloadFlag(false);
          throw error;
        }

        writeReloadFlag(true);

        // Full (non-cached) reload to pull the fresh index.html + chunk manifest.
        if (typeof window !== "undefined") {
          window.location.reload();
        }

        // Keep React Suspense in its fallback while the reload is in flight.
        return new Promise<{ default: T }>(() => {});
      }),
  );
}
